import React, { useEffect, useMemo, useState } from "react";
import { useProductContext } from "../context/ProductContext";
import { toast } from "react-toastify";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";

const SearchBox = ({ filters, setFilters, setShowResults, showDvlaResultsInline = true }) => {
    const { prod, loading, getProd } = useProductContext();
    const [searching, setSearching] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [dvlaReg, setDvlaReg] = useState("");
    const [tyreSize, setTyreSize] = useState("");
    const location = useLocation();
    const navigate = useNavigate();
    const params = new URLSearchParams(location.search);
    const tyreSizeParam = params.get("tyresize");

    const brands = useMemo(
        () => [...new Set(prod.map((p) => p.Attributes?.Brand))].filter(Boolean),
        [prod]
    );
    const brandCategories = useMemo(
        () =>
            [...new Set(prod.map((p) => p.Attributes?.BrandCategory))].filter(Boolean),
        [prod]
    );
    const categories = useMemo(
        () =>
            [...new Set(prod.map((p) => p.Attributes?.Category))].filter(Boolean),
        [prod]
    );
    const fuelEfficiency = useMemo(
        () =>
            [...new Set(prod.map((p) => p.Attributes?.FuelEfficiencyRating))].filter(
                Boolean
            ),
        [prod]
    );
    const seasons = useMemo(
        () => [...new Set(prod.map((p) => p.Attributes?.Season))].filter(Boolean),
        [prod]
    );
    const wetGrip = useMemo(
        () =>
            [...new Set(prod.map((p) => p.Attributes?.WetGripRating))].filter(Boolean),
        [prod]
    );
    const snowGrip = ["Yes", "No"];

    const tyreSizePattern = /^\d{3}\/\d{2}R\d{2}$/i;

    const handleTyreSizeChange = (e) => {
        let value = e.target.value.toUpperCase().replace(/[^0-9R/]/g, "");
        const raw = value.replace(/[^\d]/g, "");

        if (raw.length <= 3) value = raw;
        else if (raw.length <= 5) value = `${raw.slice(0, 3)}/${raw.slice(3)}`;
        else value = `${raw.slice(0, 3)}/${raw.slice(3, 5)}R${raw.slice(5, 7)}`;

        if (value.length > 9) value = value.slice(0, 9);
        setTyreSize(value);
    };

    useEffect(() => {
        if (tyreSizeParam) {
            let value = tyreSizeParam.toUpperCase().replace(/[^0-9R/]/g, "");
            const raw = value.replace(/[^\d]/g, "");
            if (raw.length <= 3) value = raw;
            else if (raw.length <= 5) value = `${raw.slice(0, 3)}/${raw.slice(3)}`;
            else value = `${raw.slice(0, 3)}/${raw.slice(3, 5)}R${raw.slice(5, 7)}`;
            if (value.length > 9) value = value.slice(0, 9);
            setTyreSize(value);
        }
    }, [tyreSizeParam, getProd]);

    useEffect(() => {
        if (location.pathname === "/" && tyreSize) {
            const cleanTyreSize = tyreSize.replace(/[^0-9]/g, "");
            const currentParams = new URLSearchParams(location.search);
            const currentTyre = currentParams.get("tyresize");
            if (currentTyre !== cleanTyreSize) {
                navigate(`/?tyresize=${cleanTyreSize}`, { replace: true });
            }
        }
    }, [location.pathname, tyreSize, navigate]);

    const handleSearch = async () => {
        const hasFilters = filters.brand || filters.brandCategory || filters.category || filters.fuelEfficiency || filters.season ||
            filters.wetGrip || filters.snowGrip;

        if (tyreSize) {
            if (!tyreSizePattern.test(tyreSize)) {
                toast.error("Please enter a valid tyre size (e.g. 205/55R16)");
                return;
            }
            if (prod.length > 0 && hasFilters) {
                const filtered = prod.filter((p) => {
                    const a = p.Attributes || {};
                    return (
                        (!filters.brand || a.Brand === filters.brand) &&
                        (!filters.brandCategory || a.BrandCategory === filters.brandCategory) &&
                        (!filters.category || a.Category === filters.category) &&
                        (!filters.fuelEfficiency || a.FuelEfficiencyRating === filters.fuelEfficiency) &&
                        (!filters.season || a.Season === filters.season) &&
                        (!filters.wetGrip || a.WetGripRating === filters.wetGrip) &&
                        (!filters.snowGrip || a.SnowGrip === filters.snowGrip)
                    );
                });
                if (filtered.length > 0) {
                    toast.success("Showing filtered products.");
                    setShowResults(true);
                    setHasSearched(true);
                } else {
                    toast.error("No products found for selected filters.");
                    setShowResults(true);
                    setHasSearched(true);
                }
                return;
            }
            const searchString = tyreSize.replace(/[^0-9]/g, "");
            if (location.pathname === "/") {
                try {
                    setSearching(true);
                    const data = await getProd(searchString);
                    if (data?.length > 0) {
                        setShowResults(true);
                        setHasSearched(true);
                        const section = document.querySelector(".featured-products");
                        if (section) {
                            section.scrollIntoView({ behavior: "smooth", block: "start" });
                        }
                        navigate(`/product?tyresize=${searchString}`);
                        toast.success("Tyres fetched successfully!");
                    } else {
                        toast.error("No products found for this tyre size.");
                    }
                } catch (error) {
                    console.error("Search error:", error);
                    toast.error("Error searching tyres. Please try again.");
                } finally {
                    setSearching(false);
                }
                return;
            }
            try {
                setSearching(true);
                const data = await getProd(searchString);
                if (data?.length > 0) {
                    setShowResults(true);
                    setHasSearched(true);
                    const section = document.querySelector(".featured-products");
                    if (section) {
                        section.scrollIntoView({ behavior: "smooth", block: "start" });
                    }
                    toast.success("Tyres fetched successfully!");
                } else {
                    toast.error("No products found for this tyre size.");
                }
            } catch (error) {
                console.error("Search error:", error);
                toast.error("Error searching tyres. Please try again.");
            } finally {
                setSearching(false);
            }

            return;
        }
        const searchString = tyreSize.replace(/[^0-9]/g, "");
        let filteredProducts = prod;
        if (hasFilters || tyreSize) {
            const url = tyreSize ? `/product?tyresize=${searchString}` : "/product";
            navigate(url);
            if (hasFilters) {
                if (filteredProducts.length > 0) toast.success("Showing filtered products.");
                else toast.error("No products match your filters.");
                setShowResults(true);
                setHasSearched(true);
            }
            return;
        }
        toast.error("Please enter a tyre size or select at least one filter.");
    };


    const handleChange = (key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
        if (hasSearched) setShowResults(false);
    };

    const [vehicleDetails, setVehicleDetails] = useState(null);
    const handleVehicleSearch = async () => {
        if (!dvlaReg) {
            toast.error("Enter a valid registration number");
            return;
        }
        setSearching(true);
        try {
            const res = await axios.post(
                "https://mv-tyre.demo-customproject.com/backend/api/get-reg-num",
                { registrationNumber: dvlaReg }
            );

            if (res.status === 200 && res.data) {
                toast.success("Search Successful!");
                setVehicleDetails(res.data);
                setTimeout(() => {
                    const section = document.querySelector(".dvla_test");
                    if (section) {
                        section.scrollIntoView({ behavior: "smooth", block: "start" });
                    }
                }, 300);
            } else {
                toast.error("No vehicle details found.");
                setVehicleDetails(null);
            }
        } catch (err) {
            toast.error(err?.response?.data?.error || "Search failed");
            console.error(err);
            setVehicleDetails(null);
        } finally {
            setSearching(false);
        }
    };

    return (
        <>
            <section className="product_search_wrpr">
                <ul className="nav nav-tabs" id="myTab" role="tablist">
                    <li className="nav-item" role="presentation">
                        <button
                            className="nav-link active"
                            id="search-by-size-tab"
                            data-bs-toggle="tab"
                            data-bs-target="#search-by-size"
                            type="button"
                            role="tab"
                            aria-controls="search-by-size"
                            aria-selected="true"
                        >
                            Search by Tyre size
                        </button>
                    </li>
                    <li className="nav-item" role="presentation">
                        <button
                            className="nav-link"
                            id="search-by-car-tab"
                            data-bs-toggle="tab"
                            data-bs-target="#search-by-car"
                            type="button"
                            role="tab"
                            aria-controls="search-by-car"
                            aria-selected="false"
                        >
                            Search by Car
                        </button>
                    </li>
                </ul>

                <div className="tab-content" id="myTabContent">
                    <div
                        className="tab-pane fade show active"
                        id="search-by-size"
                        role="tabpanel"
                        aria-labelledby="search-by-size-tab"
                    >
                        <div className="search_by_car">
                            <div className="container-fluid">
                                <div className="row align-items-center">
                                    <div className="col-md-12">
                                        <div className="row">
                                            <div className={prod?.length > 0 ? "col-md-3" : "col-md-8"}>
                                                <label className="form-label">Enter Tyre Size</label>
                                                <input
                                                    type="text"
                                                    placeholder="e.g. 205/55R16"
                                                    value={tyreSize}
                                                    onChange={handleTyreSizeChange}
                                                />
                                            </div>

                                            {
                                                prod?.length > 0 ? (
                                                    <>
                                                        <div className="col-md-3">
                                                            <label>Tyre Brand</label>
                                                            <select
                                                                className="form-select"
                                                                value={filters.brand}
                                                                onChange={(e) =>
                                                                    handleChange("brand", e.target.value)
                                                                }
                                                            >
                                                                <option value="">Select</option>
                                                                {brands.map((b, i) => (
                                                                    <option key={i}>{b}</option>
                                                                ))}
                                                            </select>
                                                        </div>

                                                        <div className="col-md-3">
                                                            <label>Brand Category</label>
                                                            <select
                                                                className="form-select"
                                                                value={filters.brandCategory}
                                                                onChange={(e) =>
                                                                    handleChange("brandCategory", e.target.value)
                                                                }
                                                            >
                                                                <option value="">Select</option>
                                                                {brandCategories.map((b, i) => (
                                                                    <option key={i}>{b}</option>
                                                                ))}
                                                            </select>
                                                        </div>

                                                        <div className="col-md-3">
                                                            <label>Category</label>
                                                            <select
                                                                className="form-select"
                                                                value={filters.category}
                                                                onChange={(e) =>
                                                                    handleChange("category", e.target.value)
                                                                }
                                                            >
                                                                <option value="">Select</option>
                                                                {categories.map((c, i) => (
                                                                    <option key={i}>{c}</option>
                                                                ))}
                                                            </select>
                                                        </div>

                                                        <div className="col-md-3">
                                                            <label>Fuel Efficiency Rating</label>
                                                            <select
                                                                className="form-select m-0"
                                                                value={filters.fuelEfficiency}
                                                                onChange={(e) =>
                                                                    handleChange("fuelEfficiency", e.target.value)
                                                                }
                                                            >
                                                                <option value="">Select</option>
                                                                {fuelEfficiency.map((f, i) => (
                                                                    <option key={i}>{f}</option>
                                                                ))}
                                                            </select>
                                                        </div>

                                                        <div className="col-md-3">
                                                            <label>Season</label>
                                                            <select
                                                                className="form-select m-0"
                                                                value={filters.season}
                                                                onChange={(e) =>
                                                                    handleChange("season", e.target.value)
                                                                }
                                                            >
                                                                <option value="">Select</option>
                                                                {seasons.map((s, i) => (
                                                                    <option key={i}>{s}</option>
                                                                ))}
                                                            </select>
                                                        </div>

                                                        <div className="col-md-2">
                                                            <label>Wet Grip Rating</label>
                                                            <select
                                                                className="form-select m-0"
                                                                value={filters.wetGrip}
                                                                onChange={(e) =>
                                                                    handleChange("wetGrip", e.target.value)
                                                                }
                                                            >
                                                                <option value="">Select</option>
                                                                {wetGrip.map((w, i) => (
                                                                    <option key={i}>{w}</option>
                                                                ))}
                                                            </select>
                                                        </div>

                                                        <div className="col-md-2">
                                                            <label>Snow Grip</label>
                                                            <select
                                                                className="form-select m-0"
                                                                value={filters.snowGrip}
                                                                onChange={(e) =>
                                                                    handleChange("snowGrip", e.target.value)
                                                                }
                                                            >
                                                                <option value="">Select</option>
                                                                {snowGrip.map((s, i) => (
                                                                    <option key={i}>{s}</option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                    </>
                                                ) : false
                                            }
                                            <div className={prod?.length > 0 ? "col-md-2" : "col-md-4"}>
                                                <button
                                                    className={prod?.length > 0 ? 'cust_btnn' : 'cust_btn'}
                                                    onClick={handleSearch}
                                                    disabled={searching || loading}
                                                >
                                                    {
                                                        prod?.length > 0 ? searching ? "Searching in advance..." : "Advance Search" : searching ? "Searching..." : "Search"
                                                    }
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div
                        className="tab-pane fade"
                        id="search-by-car"
                        role="tabpanel"
                        aria-labelledby="search-by-car-tab"
                    >
                        <div className="search_by_car">
                            <div className="container-fluid">
                                <div className="row align-items-center">
                                    <div className="col-md-12">
                                        <div className="row">
                                            <div className="col-md-7">
                                                <label>DVLA Vehicle Enquiry</label>
                                                <input
                                                    type="text"
                                                    placeholder="Enter registration (e.g. AB12CDE)"
                                                    value={dvlaReg}
                                                    onChange={(e) =>
                                                        setDvlaReg(e.target.value.toUpperCase())
                                                    }
                                                />
                                            </div>

                                            <div className="col-md-5">
                                                <button
                                                    className="cust_btn"
                                                    onClick={handleVehicleSearch}
                                                    disabled={searching || loading}
                                                >
                                                    {searching ? "Searching..." : "Search By Registration"}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            {showDvlaResultsInline && vehicleDetails && (
                <section className="dvla_test">
                    <div className="container">
                        {vehicleDetails && (
                            <div className="row justify-content-center">
                                <div className="col-lg-10">
                                    <h3>Vehicle Details</h3>
                                    <div className="vehicle-card glass-card">
                                        <ul>
                                            <li><span>Month Of First Registration</span>{vehicleDetails.monthOfFirstRegistration}</li>
                                            <li><span>Date Of Last V5C Issued</span>{vehicleDetails.dateOfLastV5CIssued}</li>
                                            <li><span>Year Of Manufacture</span>{vehicleDetails.yearOfManufacture}</li>
                                            <li><span>Registration Number</span>{vehicleDetails.registrationNumber}</li>
                                            <li><span>Marked For Export</span>{vehicleDetails.markedForExport === false ? "NO" : "YES"}</li>
                                            <li><span>MOT Expiry Date</span>{vehicleDetails.motExpiryDate}</li>
                                            <li><span>Engine Capacity</span>{vehicleDetails.engineCapacity}</li>
                                            <li><span>Type Approval</span>{vehicleDetails.typeApproval}</li>
                                            <li><span>CO₂ Emissions</span>{vehicleDetails.co2Emissions}</li>
                                            <li><span>Tax Due Date</span>{vehicleDetails.taxDueDate}</li>
                                            <li><span>MOT Status</span>{vehicleDetails.motStatus}</li>
                                            <li><span>Fuel Type</span>{vehicleDetails.fuelType}</li>
                                            <li><span>Wheel Plan</span>{vehicleDetails.wheelplan}</li>
                                            <li><span>Tax Status</span>{vehicleDetails.taxStatus}</li>
                                            <li><span>Make</span>{vehicleDetails.make}</li>
                                            <li><span>Colour</span>{vehicleDetails.colour}</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </section>
            )}

        </>

    );
};

export default SearchBox;
